/*
    Polyfills
    ---------
    Provides utility functions and polyfills for core objects.
*/

import _ from 'lodash'

/* eslint-disable no-extend-native */

if (!Array.prototype.find) {

    /**
     * The find() method returns the value of the first element in the array that satisfies the provided testing function. Otherwise undefined is returned.
     *
     * @returns the first element in the array that satisfies the provided testing function.
     */
    Object.defineProperty(Array.prototype, 'find', {
        value: function(predicate) {
            if (this == null)
                throw new TypeError('"this" is null or not defined')


            const o = Object(this)
            const len = o.length >>> 0

            if (typeof predicate !== 'function')
                throw new TypeError('predicate must be a function')


            const thisArg = arguments[1]
            let k = 0

            while (k < len) {
                const kValue = o[k]
                if (predicate.call(thisArg, kValue, k, o))
                    return kValue


                k++
            }

            return undefined
        },
    })
}

if (!Array.prototype.flatten) {

    /**
     * Flattens multi dimensional arrays.
     *
     * @returns A flat array.
     */
    Array.prototype.flatten = function() {
        return this.reduce(function(flat, toFlatten) {
            return flat.concat(Array.isArray(toFlatten) ? toFlatten.flatten() : toFlatten)
        }, [])
    }
}

if (!Array.prototype.includes) {

    /**
     * Checks if an array contains a given element.
     *
     * @returns A flag.
     */
    Object.defineProperty(Array.prototype, 'includes', {
        value: function(searchElement, fromIndex) {

            if (this == null)
                throw new TypeError('"this" is null or not defined')


            const o = Object(this)
            const len = o.length >>> 0

            if (len === 0)
                return false


            const n = fromIndex | 0
            let k = Math.max(n >= 0 ? n : len - Math.abs(n), 0)

            while (k < len) {
                if (o[k] === searchElement)
                    return true

                k++
            }

            return false
        },
    })
}

if (!Array.prototype.forEachAsync) {

    /**
     * Allows for each behaviour in async code.
     *
     * @returns A flag.
     */
    Object.defineProperty(Array.prototype, 'forEachAsync', {
        value: async function(array, callback) {

            let arr = array
            if (!callback && _.isFunction(array)) {
                callback = array
                arr = this
            }

            for (let index = 0; index < arr.length; index++) {

                // eslint-disable-next-line no-await-in-loop
                await callback(arr[index], index, arr)
            }
        },
    })
}

if (typeof Array.isArray === 'undefined') {

    /**
     * Determines if an object is an array.
     *
     * @returns A flat array.
     */
    Array.isArray = function(obj) {
        return Object.prototype.toString.call(obj) === '[object Array]'
    }
}

if (!Object.assign) {

    /**
     * Allows merging objects (shallow copy).
     *
     * @returns A copy of the object merged with another.
     */
    Object.assign = function(target) {

        if (target == null)
            throw new TypeError('Cannot convert undefined or null to object')


        const to = Object(target)

        for (let index = 1; index < arguments.length; index++) {
            const nextSource = arguments[index]

            if (nextSource != null) {
                for (const nextKey in nextSource) {
                    // Avoid bugs when hasOwnProperty is shadowed
                    if (Object.prototype.hasOwnProperty.call(nextSource, nextKey))
                        to[nextKey] = nextSource[nextKey]

                }
            }
        }
        return to
    }
}

if (!Number.prototype.round) {

    /**
     * Rounds any number to the specified decimal places.
     *
     * @returns The rounded number.
     */
    Number.prototype.round = function(roundTo) {
        roundTo = roundTo || 5
        return parseFloat(this.toFixed(roundTo))
    }
}

if (!String.prototype.includes) {

    /**
     * Checks if one string contains another.
     *
     * @returns A flag.
     */
    String.prototype.includes = function(search, start) {

        if (typeof start !== 'number')
            start = 0


        if (start + search.length > this.length)
            return false

        return this.indexOf(search, start) !== -1

    }
}
